import { useDebounceFn } from '@vueuse/core';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Select from 'ol/interaction/Select';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { BBOX_RELOAD_DEBOUNCE_MS, BBOX_RELOAD_MAX_WAIT_MS } from '#nora-ui/map/map/constants';
import { ClusterableLayer } from '#nora-ui/map/map/layer-managers/ClusterableLayer';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { SelectedOverlayFile } from '#nora-ui/map/map/state-managers/FileStateManager';

export class FileLayerManager extends ClusterableLayer {
  readonly LAYER_ID = 'filesLayer';
  readonly LAYER_NAME = 'NORA Files';
  override readonly isClickable = true;

  private selectInteraction: Select;

  protected override CLUSTER_ICON_NAME = 'perm_media';

  private debouncedLoadCurrentBbox = useDebounceFn(async (): Promise<void> => {
    await this.map.state.file.loadCurrentBbox();
  }, BBOX_RELOAD_DEBOUNCE_MS, {
    maxWait: BBOX_RELOAD_MAX_WAIT_MS,
  });

  constructor(map: NoraMap) {
    super(map);

    this.selectInteraction = new Select({
      layers: [this.layer],
      style: this.getSelectedStyle(),
    });
  }

  override async setup(): Promise<void> {
    await super.setup();
    this.setupFileClickHandler();
    await this.setupPanHandler();
  }

  override getStyleForSingleFeature(_feature: FeatureLike): Style {
    return new Style({
      image: new CircleStyle({
        radius: 24,
        stroke: new Stroke({
          color: '#CCD8E0',
          width: 4,
        }),
        fill: new Fill({
          color: '#092020',
        }),
      }),
      text: new Text({
        text: 'perm_media',
        font: '24px "Material Icons"',
        fill: new Fill({ color: '#CCD8E0' }),
      }),
    });
  }

  private setupFileClickHandler(): void {
    const olMap = this.map.getOlMapOrThrow();
    olMap.addInteraction(this.selectInteraction);

    this.selectInteraction.on('select', async (event) => {
      const selectedFeature = event.selected.at(0);

      if (!selectedFeature) {
        this.map.state.file.clearSelectedFile();

        return;
      }

      this.onSelectFile(selectedFeature);
    });
  }

  clearSelectedFile(): void {
    this.selectInteraction.getFeatures().clear();
  }

  private async setupPanHandler(): Promise<void> {
    this.map.getOlMapOrThrow().on('moveend', async () => {
      await this.debouncedLoadCurrentBbox();
    });
  }

  private onSelectFile(feature: FeatureLike): void {
    // Clustered: unwrap the Cluster feature; unclustered: the feature is the file itself.
    const subFeatures = feature.get('features') as FeatureLike[] | undefined;
    const selectedSubFeature = subFeatures?.[0] ?? feature;

    const { id, domain, domainId, path } = getFeatureProperties<SelectedOverlayFile>(selectedSubFeature);

    if (id === undefined || domain === undefined || domainId === undefined || path === undefined) {
      return;
    }

    this.map.state.file.setSelectedFile({ id, domain, domainId, path });
  }

  /**
   * Load/reload file layer features from state.
   * Renders files from the state manager's filesGeoJson.
   */
  override load(): void {
    this.unload();

    const geoJsonFormat = new GeoJSON();

    for (const filesGeoJson of this.map.state.file.filesGeoJsonCollections) {
      this.source.addFeatures(
        geoJsonFormat.readFeatures(filesGeoJson, {
          dataProjection: MapSetup.WGS84,
          featureProjection: MapSetup.WEB_MERCATOR,
        }),
      );
    }
  }

  /**
   * Unload/clear all file layer features.
   */
  override unload(): void {
    this.source.clear();
  }

  setVisibility(visible: boolean): void {
    this.layer.setVisible(visible);
  }

  private getSelectedStyle(): Style {
    return new Style({
      image: new CircleStyle({
        radius: 24,
        stroke: new Stroke({
          color: '#36B8B8',
          width: 4,
        }),
        fill: new Fill({
          color: '#092020',
        }),
      }),
      text: new Text({
        text: 'perm_media',
        font: '24px "Material Icons"',
        fill: new Fill({ color: '#36B8B8' }),
      }),
    });
  }
}
